import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/account/Navbar";
import { showNotificationToast } from "../../utils/notificationUtils";
import { useClinicData } from "../../hooks/useClinicData";
import { useTranslation } from "react-i18next";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const AddService = ({ onClose, onServiceAdded, editServiceData, clinicId = null }) => {
  const { clinic_id, clinic_service_id } = useParams();
  const { t, i18n } = useTranslation();
  const { data: clinicData, loading: clinicDataLoading } = useClinicData();
  const [hasVAT, setHasVAT] = useState(true);
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    description_en: "",
    description_ar: "",
    service_type: "medical",
    type: "Consultation",
    service_list_id: "",
    unit: "session",
    price: "",
    discount: "",
    description: "",
    prerequisites: "",
    deliveryHours: "",
    deliveryMinutes: "",
    vat: "",
    hasDueDate: "",
  });
  const [hasDueDate, setHasDueDate] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Set default category from clinic services when data is loaded
    if (!clinicDataLoading && clinicData?.clinic?.service_lists?.length > 0) {
      const defaultService = clinicData.clinic.service_lists[0];
      setForm(prev => ({
        ...prev,
        service_list_id: prev.service_list_id || defaultService.id.toString()
      }));
    }
  }, [clinicDataLoading, clinicData]);

  useEffect(() => {

    // Handle editing from URL parameter (existing functionality)
    if (clinic_service_id) {
      setIsLoading(true);
      const token = localStorage.getItem("access_token");
      fetch(`${API_BASE_URL}/api/v1/clinic-services/${clinic_service_id}`, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
        .then((res) => res.json())
        .then((data) => {
          const s = data.data;
          // Ensure description is never null - use fallback chain
          const description =
            s.description ||
            s.service_description_en ||
            s.service_description_ar ||
            "";

          setForm({
            code: s.service_code || "",
            name_en: s.service_name_en || "",
            name_ar: s.service_name_ar || "",
            description_en: s.service_description_en || "",
            description_ar: s.service_description_ar || "",
            service_type: s.service_type || "medical",
            type: s.type || "Consultation",
            service_list_id: s.service_list_id ? s.service_list_id.toString() : "",
            unit: s.unit || "session",
            price: s.price || "",
            discount: s.discount_percentage || "",
            description: description,
            prerequisites: s.pre_requisites || "",
            deliveryHours: s.delivery_period_hours || "",
            deliveryMinutes: s.delivery_period_minutes || "",
            vat: s.vat_percentage || "",
            hasDueDate: s.has_due_date || "",
          });
          setHasVAT(!!s.has_vat);
          setIsActive(!!s.is_active);
          setHasDueDate(!!s.has_due_date);
        })
        .catch((err) => {
          console.error("Error fetching service data:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // Handle editing from prop data (new functionality for drawer)
    else if (editServiceData) {
      const s = editServiceData;
      // Ensure description is never null - use fallback chain
      const description =
        s.description ||
        s.service_description_en ||
        s.service_description_ar ||
        "";

      setForm({
        code: s.service_code || "",
        name_en: s.service_name_en || "",
        name_ar: s.service_name_ar || "",
        description_en: s.service_description_en || "",
        description_ar: s.service_description_ar || "",
        service_type: s.service_type || "medical",
        type: s.type || "Consultation",
        service_list_id: s.service_list_id ? s.service_list_id.toString() : "",
        unit: s.unit || "session",
        price: s.price || "",
        discount: s.discount_percentage || "",
        description: description,
        prerequisites: s.pre_requisites || "",
        deliveryHours: s.delivery_period_hours || "",
        deliveryMinutes: s.delivery_period_minutes || "",
        vat: s.vat_percentage || "",
        hasDueDate: s.has_due_date || "",
      });
      setHasVAT(!!s.has_vat);
      setIsActive(!!s.is_active);
      setHasDueDate(!!s.has_due_date);
    }
  }, [clinic_service_id, editServiceData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!form.service_list_id || form.service_list_id.trim() === "") {
      showNotificationToast(
        {},
        {
          type: "error",
          title: t("manageServices.create.toast.validationTitle"),
          message: t("manageServices.create.toast.selectCategory"),
          duration: 3000,
        }
      );

      // Scroll to the category field with smooth animation
      const categoryField = document.querySelector('select[name="service_list_id"]');
      if (categoryField) {
        setTimeout(() => {
          categoryField.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          // Focus after scroll completes
          setTimeout(() => {
            categoryField.focus();
          }, 300);
        }, 100);
      }
      return;
    }

    const token = localStorage.getItem("access_token");
    const currentClinicId = clinic_id || editServiceData?.clinic_id;
    const currentServiceId = clinic_service_id || editServiceData?.id;



    // Handle service_list_id - convert to number if provided, otherwise null
    const serviceListId = form.service_list_id && form.service_list_id.trim() !== ""
      ? Number(form.service_list_id)
      : null;

    const serviceData = {
      clinic_id: clinicId,
      service_code: form.code,
      service_type: form.service_type,
      service_name_en: form.name_en,
      service_name_ar: form.name_ar,
      service_description_en: form.description_en?.trim() || form.name_en,
      service_description_ar: form.description_ar?.trim() || form.name_ar,
      service_list_id: serviceListId,
      unit: form.unit,
      discount_percentage: Number(form.discount) || 0,
      price: Number(form.price) || 0,
      pre_requisites: form.prerequisites || "",
      delivery_period_hours: form.deliveryHours || null,
      delivery_period_minutes: form.deliveryMinutes || null,
      has_vat: hasVAT,
      vat_percentage: Number(form.vat) || 0,
      is_active: isActive,
      has_due_date: hasDueDate,
    };
    const isEditing = !!(clinic_service_id || editServiceData);
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `${API_BASE_URL}/api/v1/clinic-services/${currentServiceId}`
      : `${API_BASE_URL}/api/v1/clinic-services`;
    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(serviceData),
    })
      .then((res) => {
        if (!res.ok) {
          res.text().then((text) => {
            console.error("Backend error:", text);
            alert(
              text ||
              t("manageServices.create.errors.requestFailed", {
                action: isEditing
                  ? t("manageServices.create.errors.actionEdit")
                  : t("manageServices.create.errors.actionAdd"),
              })
            );
          });
          throw new Error(
            t("manageServices.create.errors.requestFailed", {
              action: isEditing
                ? t("manageServices.create.errors.actionEdit")
                : t("manageServices.create.errors.actionAdd"),
            })
          );
        }
        return res.json();
      })
      .then(() => {
        // Show success toast
        showNotificationToast(
          {},
          {
            type: "success",
            title: t("manageServices.create.toast.successTitle"),
            message: isEditing
              ? t("manageServices.create.toast.successUpdate")
              : t("manageServices.create.toast.successCreate"),
            duration: 3000,
          }
        );

        // Close the modal
        if (onClose) {
          onClose();
        }

        // Trigger refresh of services list
        if (onServiceAdded) {
          onServiceAdded();
        }

        // Navigate back to services page
        navigate(`/medical/clinic/${clinicId}/manage-services`);
      })
      .catch((err) => {
        alert(
          t("manageServices.create.errors.generic", {
            message: err.message,
          })
        );
      });
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(`/medical/clinic/${clinicId}/manage-services`);
    }
  };

  // Show loading state while fetching service data
  if (isLoading) {
    return (
      <div className="add-service-wrapper">

        <div className="add-service-container">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '16px' }}>
              {t("manageServices.create.loading")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="add-service-wrapper"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <div className="add-service-container">
        <h2 className="add-service-section-title">
          {t("manageServices.create.sections.basic")}
        </h2>
        <div className="add-service-row">
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.serviceType.label")}</label>
            <select
              name="service_type"
              value={form.service_type}
              onChange={handleChange}
              className="add-service-input"
            >
              <option value="medical">
                {t("manageServices.create.fields.serviceType.options.medical")}
              </option>
              <option value="non_medical">
                {t("manageServices.create.fields.serviceType.options.nonMedical")}
              </option>
              <option value="medication">
                {t("manageServices.create.fields.serviceType.options.medication")}
              </option>
            </select>
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.code.label")}</label>
            <input
              type="text"
              name="code"
              value={form.code}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.code.placeholder")}
              className="add-service-input"
            />
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.nameEn.label")}</label>
            <input
              type="text"
              name="name_en"
              value={form.name_en}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.nameEn.placeholder")}
              className="add-service-input"
            />
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.nameAr.label")}</label>
            <input
              type="text"
              name="name_ar"
              value={form.name_ar}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.nameAr.placeholder")}
              className="add-service-input"
            />
          </div>
        </div>

        <div className="add-service-row">
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.category.label")}</label>
            <select
              name="service_list_id"
              value={form.service_list_id}
              onChange={handleChange}
              className="add-service-input"
              disabled={clinicDataLoading}
            >
              <option key="" value="" disabled>
                {clinicDataLoading
                  ? t("manageServices.create.loading")
                  : t("manageServices.create.fields.category.placeholder")}
              </option>
              {clinicData?.clinic?.service_lists?.map((service) => (
                <option key={service.id} value={service.id}>
                  {i18n.language === "ar" ? service.name_ar || service.name_en : service.name_en || service.name_ar}
                </option>
              ))}
            </select>
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.unit.label")}</label>
            <select
              name="unit"
              value={form.unit}
              onChange={handleChange}
              className="add-service-input"
            >
              <option value="pieces">{t("manageServices.units.pieces")}</option>
              <option value="session">{t("manageServices.units.session")}</option>
              <option value="other">{t("manageServices.units.other")}</option>
            </select>
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.price.label")}</label>
            <input
              type="text"
              name="price"
              value={form.price}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.price.placeholder")}
              className="add-service-input"
            />
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.discount.label")}</label>
            <input
              type="text"
              name="discount"
              value={form.discount}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.discount.placeholder")}
              className="add-service-input"
            />
          </div>
        </div>

        <div className="add-service-row">
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.descriptionEn.label")}</label>
            <textarea
              name="description_en"
              value={form.description_en}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.descriptionEn.placeholder")}
              className="add-service-input add-service-textarea"
            />
          </div>
          <div className="add-service-col">
            <label>{t("manageServices.create.fields.descriptionAr.label")}</label>
            <textarea
              name="description_ar"
              value={form.description_ar}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.descriptionAr.placeholder")}
              className="add-service-input add-service-textarea"
            />
          </div>
        </div>

        <h2 className="add-service-section-title">
          {t("manageServices.create.sections.advanced")}
        </h2>

        <div className="add-service-row">
          <div className="add-service-col" style={{ flex: 2 }}>
            <label>{t("manageServices.create.fields.prerequisites.label")}</label>
            <input
              type="text"
              name="prerequisites"
              value={form.prerequisites}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.prerequisites.placeholder")}
              className="add-service-input"
            />
          </div>
          <div className="add-service-col add-service-delivery-period" style={{ flex: 2 }}>
            <label>{t("manageServices.create.fields.delivery.label")}</label>
            <div className="add-service-delivery-inputs">
              <input
                type="text"
                name="deliveryHours"
                value={form.deliveryHours}
                onChange={handleChange}
                placeholder={t("manageServices.create.fields.delivery.hours")}
                className="add-service-input"
              />
              <input
                type="text"
                name="deliveryMinutes"
                value={form.deliveryMinutes}
                onChange={handleChange}
                placeholder={t("manageServices.create.fields.delivery.minutes")}
                className="add-service-input"
              />
            </div>
          </div>
        </div>

        <div className="add-service-row" style={{ marginTop: '10px' }}>
          <div className="add-service-col">
            <label style={{ fontWeight: 600 }}>{t("manageServices.create.fields.isActive.label")}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label className="add-service-switch">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={() => setIsActive(!isActive)}
                />
                <span className="add-service-slider round"></span>
              </label>
              <span className={`add-service-vat-status${isActive ? " active" : ""}`}>
                {isActive ? t("manageServices.create.status.enabled") : t("manageServices.create.status.disabled")}
              </span>
            </div>
          </div>

          <div className="add-service-col">
            <label style={{ fontWeight: 600 }}>{t("manageServices.create.fields.hasDueDate.label")}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label className="add-service-switch">
                <input
                  type="checkbox"
                  checked={hasDueDate}
                  onChange={() => setHasDueDate(!hasDueDate)}
                />
                <span className="add-service-slider round"></span>
              </label>
              <span className={`add-service-vat-status${hasDueDate ? " active" : ""}`}>
                {hasDueDate ? t("manageServices.create.status.yes") : t("manageServices.create.status.no")}
              </span>
            </div>
          </div>

          <div className="add-service-col">
            <label style={{ fontWeight: 600 }}>{t("manageServices.create.fields.hasVat.label")}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label className="add-service-switch">
                <input
                  type="checkbox"
                  checked={hasVAT}
                  onChange={() => setHasVAT(!hasVAT)}
                />
                <span className="add-service-slider round"></span>
              </label>
              <span className={`add-service-vat-status${hasVAT ? " active" : ""}`}>
                {hasVAT ? t("manageServices.create.status.yes") : t("manageServices.create.status.no")}
              </span>
            </div>
          </div>

          <div className="add-service-col">
            <label style={{ fontWeight: 600 }}>{t("manageServices.create.fields.vat.label")}</label>
            <input
              type="text"
              name="vat"
              value={form.vat}
              onChange={handleChange}
              placeholder={t("manageServices.create.fields.vat.placeholder")}
              className="add-service-input"
              disabled={!hasVAT}
              style={{
                width: '100%',
                ...(!hasVAT ? { backgroundColor: "#f0f0f0", color: "#a0a0a0", cursor: "not-allowed" } : {})
              }}
            />
          </div>
        </div>

        <div className="add-service-btn-row">
          <button className="add-service-btn add-service-btn-primary" onClick={handleSubmit}>
            {clinic_service_id || editServiceData ? t("manageServices.create.buttons.save") : t("manageServices.create.buttons.add")}
          </button>
          <button className="add-service-btn add-service-btn-secondary" onClick={handleBack}>
            {t("manageServices.create.buttons.back")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddService;
